import { Link } from "react-router-dom";
import { ShieldCheck, Mail, FileText, Users, Clock, Lock, Cookie, RefreshCw } from "lucide-react";
import { Reveal, MaskedLine } from "../components/Reveal";
import Seo from "../components/Seo";

const SECTIONS = [
  {
    icon: FileText, color: "#10B981", title: "The information we collect",
    body: [
      "When you contact us through a form on this website, we collect the details you provide — typically your name, organisation, email address, telephone number and the details of your enquiry.",
      "If you apply to join our pharmacy team, we also collect the professional information you provide, including your profession, registration number, experience, cover message and your uploaded CV.",
      "Our forms ask you not to include patient-identifiable or confidential clinical information, and we kindly ask that you do not send it to us.",
    ],
  },
  {
    icon: Users, color: "#06B6D4", title: "How we use your information",
    body: [
      "We use the information you provide only to respond to your enquiry, to discuss or provide our services, and — for job applicants — to review your application and contact you about suitable opportunities.",
      "We do not sell your information, and we do not share it with third parties for their own marketing purposes.",
    ],
  },
  {
    icon: Lock, color: "#F59E0B", title: "Who can access it and how it is protected",
    body: [
      "Your information is stored securely and is only accessible to authorised members of our team through access-controlled systems, including the secure hosting and email providers that operate this website on our behalf.",
      "We apply appropriate technical and organisational measures to protect the information we hold.",
    ],
  },
  {
    icon: Clock, color: "#EF4444", title: "How long we keep it",
    body: [
      "We keep personal information only for as long as it is needed for the purpose it was collected for — for example, to progress your enquiry or review your application — after which it is securely deleted.",
    ],
  },
  {
    icon: ShieldCheck, color: "#6366F1", title: "Your rights",
    body: [
      "You can ask us at any time to confirm what information we hold about you, to correct it, or to delete it. You can also object to us using your information.",
      "To exercise any of these rights, contact us using the email address below. If you are unhappy with how we handle your information, you also have the right to raise a concern with the Information Commissioner's Office (ico.org.uk).",
    ],
  },
  {
    icon: Cookie, color: "#10B981", title: "Cookies",
    body: [
      "This website does not set marketing or analytics cookies. The platform that serves this website may set strictly necessary security cookies that keep the site safe and available.",
    ],
  },
  {
    icon: RefreshCw, color: "#06B6D4", title: "Changes to this policy",
    body: [
      "We may update this privacy policy from time to time. The latest version will always be published on this page.",
    ],
  },
];

const PrivacyPolicy = () => (
  <main data-testid="privacy-page">
    <Seo
      title="Privacy Policy | National Clinical Services"
      description="How National Clinical Services Ltd collects, uses and protects your personal information when you contact us or apply to join our pharmacy team."
    />

    {/* HERO */}
    <section className="relative overflow-hidden pt-32 sm:pt-40 pb-14 sm:pb-16">
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <span className="inline-flex items-center gap-2 bg-white border border-[#E2E8F0] rounded-full px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-[#10B981]">
            <ShieldCheck size={13} /> Your Privacy
          </span>
        </Reveal>
        <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#0B192C]">
          <MaskedLine delay={0.05}>Privacy Policy</MaskedLine>
        </h1>
        <Reveal delay={0.15}>
          <p className="mt-5 max-w-2xl text-sm sm:text-base leading-relaxed text-[#475569]">
            This policy explains, in plain terms, what information National Clinical Services Ltd
            collects through this website, why we collect it, who can access it and the rights you
            have over it. Last updated: October 2026.
          </p>
        </Reveal>
      </div>
    </section>

    {/* SECTIONS */}
    <section className="pb-16 sm:pb-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-5">
        {SECTIONS.map((s, i) => (
          <Reveal key={s.title} delay={0.04 * i}>
            <div className="bg-white border border-[#E2E8F0] rounded-3xl p-7 sm:p-9 shadow-[0_10px_30px_rgba(11,25,44,0.04)]"
              data-testid={`privacy-section-${i}`}>
              <div className="flex items-center gap-4">
                <span className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
                  style={{ backgroundColor: `${s.color}14`, color: s.color }}>
                  <s.icon size={20} />
                </span>
                <h2 className="font-display text-base md:text-lg font-extrabold text-[#0B192C]">{s.title}</h2>
              </div>
              <div className="mt-4 space-y-3">
                {s.body.map((p, j) => (
                  <p key={j} className="text-sm leading-relaxed text-[#475569]">{p}</p>
                ))}
              </div>
            </div>
          </Reveal>
        ))}

        <Reveal delay={0.1}>
          <div className="bg-[#0B192C] rounded-3xl p-7 sm:p-9" data-testid="privacy-contact-card">
            <div className="flex items-center gap-4">
              <span className="w-11 h-11 rounded-2xl bg-[#10B981]/15 text-[#10B981] flex items-center justify-center shrink-0">
                <Mail size={20} />
              </span>
              <h2 className="font-display text-base md:text-lg font-extrabold text-white">Contact us about your data</h2>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-white/70">
              For any question about this policy, or to request access to, correction of, or deletion
              of your information, email us at{" "}
              <a href="mailto:yasin@nationalclinicalservices.co.uk" data-testid="privacy-contact-email"
                className="font-bold text-[#10B981] hover:text-white transition-colors">
                yasin@nationalclinicalservices.co.uk
              </a>
              . You can also reach us through our{" "}
              <Link to="/contact" data-testid="privacy-contact-link"
                className="font-bold text-[#10B981] hover:text-white transition-colors">
                contact page
              </Link>
              .
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  </main>
);

export default PrivacyPolicy;
